import { Card, Stack, Table, Text, Title } from '@mantine/core';
import { RISQ_PREP_TIMINGS, RISQ_WORK_TYPES, visibleWorkTypes } from '@opti-sea/risq';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useWorkplace } from '../../../core/access';
import { unwrap, useDb } from '../../../core/supabase';
import { QueryState, usePositionNames } from '../../../ui/components';
import { formatDateTime } from '../../../ui/format';

type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

function field(value: Json, key: string): Json | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value[key]
    : undefined;
}

function strings(value: Json | undefined): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** Layer history of one question (who, when, before → after, which layer). */
export function LayerHistory({ questionKey }: { questionKey: string }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const db = useDb();
  const { company, vessels } = useWorkplace();
  const { list } = usePositionNames();
  const history = useQuery({
    queryKey: ['history', company.id, questionKey],
    queryFn: async () => {
      const rows = unwrap(
        await db
          .from('risq_layer_changes')
          .select(
            'id, vessel_id, target, action, item_key, old_value, new_value, actor_id, actor_role, created_at',
          )
          .eq('company_id', company.id)
          .eq('question_key', questionKey)
          .order('created_at', { ascending: false }),
      );
      const ids = [
        ...new Set(rows.map((r) => r.actor_id).filter((id): id is string => id !== null)),
      ];
      const people =
        ids.length === 0
          ? []
          : unwrap(await db.from('profiles').select('id, full_name').in('id', ids));
      return { rows, names: new Map(people.map((p) => [p.id, p.full_name])) };
    },
  });
  const vesselName = (id: string | null) =>
    id === null ? t('editor.wholeCompany') : (vessels.find((v) => v.id === id)?.name ?? id);
  const value = (target: string, v: Json) => {
    if (v === null) {
      return t('history.nothing');
    }
    const source = field(v, 'source');
    const from =
      typeof source === 'string'
        ? t('history.from', {
            source: t(
              `layers.source.${source === 'company' || source === 'vessel' ? source : 'base'}`,
            ),
          })
        : '';
    if (target === 'positions') {
      return `${list(strings(field(v, 'positions')), t('questions.unassigned'))}${from}`;
    }
    if (target === 'flags') {
      const work = visibleWorkTypes(
        strings(field(v, 'work_types')).filter((w): w is (typeof RISQ_WORK_TYPES)[number] =>
          (RISQ_WORK_TYPES as readonly string[]).includes(w),
        ),
      );
      const timing = strings(field(v, 'prep_timing')).filter(
        (p): p is (typeof RISQ_PREP_TIMINGS)[number] =>
          (RISQ_PREP_TIMINGS as readonly string[]).includes(p),
      );
      return `${[
        ...work.map((w) => t(`common:workTypes.${w}.name`)),
        ...(timing.length === 0
          ? [t('questions.timingDefault')]
          : timing.map((p) => t(`common:timings.${p}.short`))),
      ].join(', ')}${from}`;
    }
    const text = field(v, 'text_en');
    const hidden = field(v, 'hidden') === true;
    return `${typeof text === 'string' ? text : ''}${hidden ? t('history.hidden') : ''}${from}`;
  };
  return (
    <Card withBorder>
      <Stack>
        <Title order={2} size="h3">
          {t('history.title')}
        </Title>
        <QueryState loading={history.isPending} error={history.error}>
          {() =>
            (history.data?.rows ?? []).length === 0 ? (
              <Text size="sm">{t('history.empty')}</Text>
            ) : (
              <Table.ScrollContainer minWidth={760}>
                <Table verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('activity.when')}</Table.Th>
                      <Table.Th>{t('activity.who')}</Table.Th>
                      <Table.Th>{t('editor.where')}</Table.Th>
                      <Table.Th>{t('history.what')}</Table.Th>
                      <Table.Th>{t('history.before')}</Table.Th>
                      <Table.Th>{t('history.after')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(history.data?.rows ?? []).map((r) => (
                      <Table.Tr key={r.id}>
                        <Table.Td>{formatDateTime(r.created_at)}</Table.Td>
                        <Table.Td>
                          {t('history.actor', {
                            name:
                              r.actor_id === null
                                ? t('activity.system')
                                : (history.data?.names.get(r.actor_id) ?? t('activity.someone')),
                            role: t(
                              `history.roles.${r.actor_role === 'admin' || r.actor_role === 'dpa' || r.actor_role === 'master' ? r.actor_role : 'service'}`,
                            ),
                          })}
                        </Table.Td>
                        <Table.Td>{vesselName(r.vessel_id)}</Table.Td>
                        <Table.Td>
                          {t(`history.targets.${r.target}`)} · {t(`history.actions.${r.action}`)}
                        </Table.Td>
                        <Table.Td>{value(r.target, r.old_value)}</Table.Td>
                        <Table.Td>{value(r.target, r.new_value)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )
          }
        </QueryState>
      </Stack>
    </Card>
  );
}
